import { ArrowDownward, ArrowUpward, DeleteForever, VisibilityOff } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TranslateText } from '../../../../../../application/language/TranslateText';
import { Colors } from '../../../../color/Colors';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ITheoryBlock } from '../../../../../../service/unit/interface/IUnitTheory';
import { IParagraph } from '../../../../../../service/unit/interface/theoryBlocks/IParagraph';

import { Editor } from '../../../../quillTextEditor/Editor';

export interface ParagraphComponentProps {
  data: ITheoryBlock<IParagraph>;
  onDelete: () => void;
  onUp: () => void;
  onDown: () => void;
}

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    background: Colors.bgSurface,
    padding: '16px 42px',
    '&:hover $actions': {
      visibility: 'visible',
    },
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '20px',
    alignItems: 'flex-end',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    visibility: 'hidden',
  },
  title: {
    marginRight: 'auto',
  },
  preview: {
    minHeight: 140,
    padding: '12px 14px',
    borderRadius: 8,
    border: `1px solid ${Colors.border}`,
    cursor: 'text',
    overflow: 'hidden',
  },
}));

export const ParagraphComponent = observer(({ data: blockData, onDelete, onUp, onDown }: ParagraphComponentProps) => {
  const classes = useStyles();
  const editorRef = useRef<any>(null);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const { data: paragraph, pseudoId } = blockData;
  const [isEditorMounted, setIsEditorMounted] = useState(false);

  const handleParagraphChange = useCallback(
    (newValue: string) => {
      paragraph.text = newValue;
    },
    [paragraph],
  );

  useEffect(() => {
    if (isEditorMounted) {
      return;
    }

    const previewElement = previewContainerRef.current;
    if (!previewElement) {
      setIsEditorMounted(true);
      return;
    }

    if (typeof window === 'undefined' || !window.IntersectionObserver) {
      setIsEditorMounted(true);
      return;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        const isInViewport = entries.some((entry) => entry.isIntersecting);
        if (isInViewport) {
          setIsEditorMounted(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: '300px 0px',
        threshold: 0.01,
      },
    );

    observer.observe(previewElement);

    return () => {
      observer.disconnect();
    };
  }, [isEditorMounted, pseudoId]);

  return (
    <Box key={pseudoId} className={classes.container}>
      <Box className={classes.headerRow}>
        <Typography className={classes.title}>
          <TranslateText i18nKey="createUnit.p" />
        </Typography>

        <Box className={`${classes.actions} action`}>
          <DeleteForever fontSize="small" sx={{ cursor: 'pointer' }} onClick={onDelete} />
          <VisibilityOff fontSize="small" sx={{ cursor: 'pointer' }} onClick={() => {}} />
          <ArrowUpward fontSize="small" sx={{ cursor: 'pointer' }} onClick={onUp} />
          <ArrowDownward fontSize="small" sx={{ cursor: 'pointer' }} onClick={onDown} />
        </Box>
      </Box>

      {isEditorMounted ? (
        <Editor ref={editorRef} defaultValue={paragraph.text} onTextChange={handleParagraphChange} />
      ) : (
        <Box
          ref={previewContainerRef}
          className={classes.preview}
          onClick={() => {
            setIsEditorMounted(true);
          }}
        >
          {paragraph.text ? (
            <div dangerouslySetInnerHTML={{ __html: paragraph.text }} />
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              <TranslateText i18nKey="createUnit.p" />
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
});
